/**
 * Shared GCash payment-proof preview — used by /sports/bookings, /sports/walk-in,
 * and /sports/monitoring so they don't drift apart.
 *
 * A booking only has a proof when the customer uploaded a GCash screenshot
 * (manual payment). PayMongo-confirmed bookings have no `proof_key` and simply
 * won't render the button — which is exactly what we want.
 */
import { useEffect, useState } from 'react';
import { Image } from 'lucide-react';
import { sportsApi } from '../SportsAuthContext';

/** Full-screen modal that lazily fetches a presigned proof-image URL. */
export function ProofModal({ bookingId, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    sportsApi.get(`/sports/settings/proof-url/${bookingId}`)
      .then(r => { if (alive) setUrl(r.data.url); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [bookingId]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }}
      onClick={onClose}>
      <div className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #2A2A2A', maxWidth: 480, width: '100%' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
          <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>GCash Proof</p>
          <button onClick={onClose} style={{ color: '#737373', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div className="p-5 flex items-center justify-center" style={{ minHeight: 200 }}>
          {loading && <div className="animate-spin h-8 w-8 rounded-full border-2 border-[#E63946] border-t-transparent" />}
          {error && <p className="text-sm" style={{ color: '#737373' }}>Could not load proof image.</p>}
          {url && <img src={url} alt="GCash proof" className="max-h-80 rounded-lg object-contain w-full" />}
        </div>
      </div>
    </div>
  );
}

/**
 * Self-contained "View Proof" button that owns its own modal state.
 * Drop it anywhere a booking has a `proof_key`:
 *   {booking.proof_key && <ProofPreviewButton bookingId={booking.id} />}
 */
export default function ProofPreviewButton({ bookingId, label = 'View Proof', style }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid={`proof-preview-btn-${bookingId}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
          background: '#1E1E1E', border: '1px solid #2A2A2A', color: '#A3A3A3',
          fontSize: 12, fontWeight: 700,
          ...style,
        }}>
        <Image style={{ width: 14, height: 14 }} /> {label}
      </button>
      {open && <ProofModal bookingId={bookingId} onClose={() => setOpen(false)} />}
    </>
  );
}
